/**
 * Model Analyzer - 3D模型分析工具
 * 用于分析摩托车主体和改装件的尺寸、结构，确定如何组装
 */

import { useState, useRef, useEffect, Suspense } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Environment, useGLTF, Html, Center, Grid, GizmoHelper, GizmoViewport } from '@react-three/drei';
import * as THREE from 'three';
import { getR2BaseUrl } from '../../config/assets';

// R2 CDN URL（生产环境必须配置 VITE_R2_BASE_URL）
const R2_BASE = getR2BaseUrl();
const R2_MODEL_URL = R2_BASE ? `${R2_BASE}/models/harley_davidson_xr1200x.glb` : null;

// 模型 URL：生产环境使用 R2 CDN，开发环境使用本地文件
const bikeModelUrl = R2_MODEL_URL || '/assets/3D/harley_davidson_xr1200x.glb';

// ============================================
// 模型信息分析组件
// ============================================
function ModelInfo({ scene, name }) {
    const [info, setInfo] = useState(null);

    useEffect(() => {
        if (!scene) return;

        const box = new THREE.Box3().setFromObject(scene);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        // 收集所有mesh名称
        const meshNames = [];
        const materialNames = new Set();

        scene.traverse((child) => {
            if (child.isMesh) {
                meshNames.push(child.name || 'unnamed');
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(m => materialNames.add(m.name || 'unnamed'));
                    } else {
                        materialNames.add(child.material.name || 'unnamed');
                    }
                }
            }
        });

        setInfo({
            size,
            center,
            meshCount: meshNames.length,
            meshNames: meshNames.slice(0, 20), // 只显示前20个
            materialCount: materialNames.size,
            materials: Array.from(materialNames).slice(0, 10)
        });
    }, [scene]);

    if (!info) return null;

    return (
        <div style={{
            background: 'rgba(0,0,0,0.85)',
            padding: 15,
            borderRadius: 8,
            fontSize: 12,
            maxHeight: 400,
            overflowY: 'auto'
        }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#ff6600' }}>{name}</h4>

            <div style={{ marginBottom: 10 }}>
                <strong>尺寸 (Size):</strong>
                <div style={{ color: '#88ff88', fontFamily: 'monospace' }}>
                    X: {info.size.x.toFixed(3)} | Y: {info.size.y.toFixed(3)} | Z: {info.size.z.toFixed(3)}
                </div>
            </div>

            <div style={{ marginBottom: 10 }}>
                <strong>中心点 (Center):</strong>
                <div style={{ color: '#88ff88', fontFamily: 'monospace' }}>
                    X: {info.center.x.toFixed(3)} | Y: {info.center.y.toFixed(3)} | Z: {info.center.z.toFixed(3)}
                </div>
            </div>

            <div style={{ marginBottom: 10 }}>
                <strong>网格数量:</strong> {info.meshCount}
            </div>

            <div style={{ marginBottom: 10 }}>
                <strong>材质数量:</strong> {info.materialCount}
            </div>

            <div>
                <strong>部件名称 (前20个):</strong>
                <div style={{ fontSize: 10, color: '#aaa', maxHeight: 100, overflowY: 'auto' }}>
                    {info.meshNames.map((name, i) => (
                        <div key={i}>• {name}</div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ============================================
// 摩托车模型
// ============================================
function BikeModel({ scale, position, rotation, visible, onSceneReady }) {
    const { scene } = useGLTF(bikeModelUrl);

    useEffect(() => {
        if (scene && onSceneReady) {
            onSceneReady(scene);
        }
    }, [scene, onSceneReady]);

    if (!visible) return null;

    return (
        <primitive
            object={scene}
            scale={scale}
            position={position}
            rotation={rotation}
        />
    );
}

// ============================================
// 排气管模型
// ============================================
/*
function ExhaustModel({ scale, position, rotation, visible, onSceneReady }) {
    const { scene } = useGLTF(exhaustModelUrl);

    useEffect(() => {
        if (scene && onSceneReady) {
            onSceneReady(scene);
        }
    }, [scene, onSceneReady]);

    if (!visible) return null;

    return (
        <primitive
            object={scene}
            scale={scale}
            position={position}
            rotation={rotation}
        />
    );
}
*/

// ============================================
// 加载指示器
// ============================================
function Loader() {
    return (
        <Html center>
            <div style={{ color: 'white', textAlign: 'center' }}>
                <div style={{
                    width: 50,
                    height: 50,
                    border: '3px solid #333',
                    borderTopColor: '#ff6600',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 15px'
                }} />
                <p>Loading 3D Models...</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        </Html>
    );
}

// ============================================
// 主页面
// ============================================
export default function ModelAnalyzer() {
    // 摩托车参数
    const [bikeVisible, setBikeVisible] = useState(true);
    const [bikeScale, setBikeScale] = useState(0.01);
    const [bikePosition, setBikePosition] = useState({ x: 0, y: 0, z: 0 });
    const [bikeRotation, setBikeRotation] = useState({ x: 0, y: 0, z: 0 });
    const [bikeScene, setBikeScene] = useState(null);

    // 排气管参数
    const [exhaustVisible, setExhaustVisible] = useState(true);
    const [exhaustScale, setExhaustScale] = useState(5);
    const [exhaustPosition, setExhaustPosition] = useState({ x: 0, y: 0, z: 0.5 });
    const [exhaustRotation, setExhaustRotation] = useState({ x: 0, y: 0, z: 0 });
    const [exhaustScene, setExhaustScene] = useState(null);

    // 导出配置
    const exportConfig = () => {
        const config = {
            bike: {
                scale: bikeScale,
                position: bikePosition,
                rotation: bikeRotation
            },
            exhaust: {
                scale: exhaustScale,
                position: exhaustPosition,
                rotation: exhaustRotation
            }
        };
        console.log('=== 配置数据 ===');
        console.log(JSON.stringify(config, null, 2));
        alert('配置已输出到控制台 (F12 查看)');
    };

    return (
        <div style={{
            display: 'flex',
            height: '100vh',
            background: '#1a1a1a',
            color: 'white'
        }}>
            {/* 3D 视图 */}
            <div style={{ flex: 1, position: 'relative' }}>
                <Canvas camera={{ position: [5, 3, 5], fov: 50 }}>
                    <Suspense fallback={<Loader />}>
                        <ambientLight intensity={0.5} />
                        <directionalLight position={[10, 10, 5]} intensity={1} />
                        <Environment preset="city" />

                        {/* 网格辅助线 */}
                        <Grid
                            args={[10, 10]}
                            cellSize={0.5}
                            cellColor="#444"
                            sectionSize={1}
                            sectionColor="#666"
                            fadeDistance={20}
                        />

                        {/* 坐标轴 */}
                        <axesHelper args={[2]} />

                        {/* 摩托车 */}
                        <BikeModel
                            visible={bikeVisible}
                            scale={bikeScale}
                            position={[bikePosition.x, bikePosition.y, bikePosition.z]}
                            rotation={[bikeRotation.x, bikeRotation.y, bikeRotation.z]}
                            onSceneReady={setBikeScene}
                        />

                        {/* 排气管 */}
                        {/* 
                        <ExhaustModel
                            visible={exhaustVisible}
                            scale={exhaustScale}
                            position={[exhaustPosition.x, exhaustPosition.y, exhaustPosition.z]}
                            rotation={[exhaustRotation.x, exhaustRotation.y, exhaustRotation.z]}
                            onSceneReady={setExhaustScene}
                        /> 
                        */}

                        <OrbitControls />

                        {/* 视角指示器 */}
                        <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
                            <GizmoViewport />
                        </GizmoHelper>
                    </Suspense>
                </Canvas>

                {/* 标题 */}
                <div style={{
                    position: 'absolute',
                    top: 20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0,0,0,0.8)',
                    padding: '10px 30px',
                    borderRadius: 8,
                    textAlign: 'center'
                }}>
                    <h2 style={{ margin: 0, color: '#ff6600' }}>3D 模型分析器</h2>
                    <p style={{ margin: '5px 0 0', fontSize: 12, color: '#888' }}>
                        调整参数，找到排气管正确安装位置
                    </p>
                </div>
            </div>

            {/* 控制面板 */}
            <div style={{
                width: 350,
                background: '#2a2a2a',
                padding: 20,
                overflowY: 'auto',
                borderLeft: '1px solid #444'
            }}>
                {/* 摩托车控制 */}
                <div style={{ marginBottom: 30 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15 }}>
                        <input
                            type="checkbox"
                            checked={bikeVisible}
                            onChange={(e) => setBikeVisible(e.target.checked)}
                            style={{ marginRight: 10 }}
                        />
                        <h3 style={{ margin: 0, color: '#4a9eff' }}>🏍️ 摩托车主体</h3>
                    </div>

                    <div style={{ marginBottom: 10 }}>
                        <label>缩放 Scale: {bikeScale.toFixed(4)}</label>
                        <input
                            type="range"
                            min="0.001"
                            max="0.05"
                            step="0.001"
                            value={bikeScale}
                            onChange={(e) => setBikeScale(parseFloat(e.target.value))}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ marginBottom: 10 }}>
                        <label>Y 位置: {bikePosition.y.toFixed(2)}</label>
                        <input
                            type="range"
                            min="-2"
                            max="2"
                            step="0.05"
                            value={bikePosition.y}
                            onChange={(e) => setBikePosition({ ...bikePosition, y: parseFloat(e.target.value) })}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ marginBottom: 10 }}>
                        <label>Y 旋转: {(bikeRotation.y * 180 / Math.PI).toFixed(0)}°</label>
                        <input
                            type="range"
                            min={-Math.PI}
                            max={Math.PI}
                            step="0.1"
                            value={bikeRotation.y}
                            onChange={(e) => setBikeRotation({ ...bikeRotation, y: parseFloat(e.target.value) })}
                            style={{ width: '100%' }}
                        />
                    </div>

                    {/* 模型信息 */}
                    {bikeScene && <ModelInfo scene={bikeScene} name="harley-davidson_police.glb" />}
                </div>

                {/* 排气管控制 */}
                <div style={{ marginBottom: 30 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15 }}>
                        <input
                            type="checkbox"
                            checked={exhaustVisible}
                            onChange={(e) => setExhaustVisible(e.target.checked)}
                            style={{ marginRight: 10 }}
                        />
                        <h3 style={{ margin: 0, color: '#ff6600' }}>🔧 排气管改装件</h3>
                    </div>

                    <div style={{ marginBottom: 10 }}>
                        <label>缩放 Scale: {exhaustScale.toFixed(2)}</label>
                        <input
                            type="range"
                            min="0.1"
                            max="20"
                            step="0.1"
                            value={exhaustScale}
                            onChange={(e) => setExhaustScale(parseFloat(e.target.value))}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ marginBottom: 10 }}>
                        <label>X 位置: {exhaustPosition.x.toFixed(2)}</label>
                        <input
                            type="range"
                            min="-3"
                            max="3"
                            step="0.05"
                            value={exhaustPosition.x}
                            onChange={(e) => setExhaustPosition({ ...exhaustPosition, x: parseFloat(e.target.value) })}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ marginBottom: 10 }}>
                        <label>Y 位置: {exhaustPosition.y.toFixed(2)}</label>
                        <input
                            type="range"
                            min="-2"
                            max="2"
                            step="0.05"
                            value={exhaustPosition.y}
                            onChange={(e) => setExhaustPosition({ ...exhaustPosition, y: parseFloat(e.target.value) })}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ marginBottom: 10 }}>
                        <label>Z 位置: {exhaustPosition.z.toFixed(2)}</label>
                        <input
                            type="range"
                            min="-3"
                            max="3"
                            step="0.05"
                            value={exhaustPosition.z}
                            onChange={(e) => setExhaustPosition({ ...exhaustPosition, z: parseFloat(e.target.value) })}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ marginBottom: 10 }}>
                        <label>X 旋转: {(exhaustRotation.x * 180 / Math.PI).toFixed(0)}°</label>
                        <input
                            type="range"
                            min={-Math.PI}
                            max={Math.PI}
                            step="0.1"
                            value={exhaustRotation.x}
                            onChange={(e) => setExhaustRotation({ ...exhaustRotation, x: parseFloat(e.target.value) })}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ marginBottom: 10 }}>
                        <label>Y 旋转: {(exhaustRotation.y * 180 / Math.PI).toFixed(0)}°</label>
                        <input
                            type="range"
                            min={-Math.PI}
                            max={Math.PI}
                            step="0.1"
                            value={exhaustRotation.y}
                            onChange={(e) => setExhaustRotation({ ...exhaustRotation, y: parseFloat(e.target.value) })}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ marginBottom: 10 }}>
                        <label>Z 旋转: {(exhaustRotation.z * 180 / Math.PI).toFixed(0)}°</label>
                        <input
                            type="range"
                            min={-Math.PI}
                            max={Math.PI}
                            step="0.1"
                            value={exhaustRotation.z}
                            onChange={(e) => setExhaustRotation({ ...exhaustRotation, z: parseFloat(e.target.value) })}
                            style={{ width: '100%' }}
                        />
                    </div>

                    {/* 模型信息 */}
                    {exhaustScene && <ModelInfo scene={exhaustScene} name="harley_davidson_exhaust_high_poly.glb" />}
                </div>

                {/* 导出按钮 */}
                <button
                    onClick={exportConfig}
                    style={{
                        width: '100%',
                        padding: 15,
                        fontSize: 16,
                        background: '#ff6600',
                        border: 'none',
                        borderRadius: 8,
                        color: 'white',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                    }}
                >
                    📋 导出配置到控制台
                </button>

                {/* 说明 */}
                <div style={{
                    marginTop: 20,
                    padding: 15,
                    background: 'rgba(255,102,0,0.1)',
                    border: '1px solid #ff6600',
                    borderRadius: 8,
                    fontSize: 12
                }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#ff6600' }}>使用说明</h4>
                    <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8 }}>
                        <li>先调整摩托车的缩放和位置</li>
                        <li>然后调整排气管，让它贴合到正确位置</li>
                        <li>点击"导出配置"保存参数</li>
                        <li>这些参数将用于最终的配置器</li>
                    </ol>
                </div>
            </div>
        </div>
    );
}
